// src/components/EntryModal.js
import React, { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";

function EntryModal({ isOpen, onClose }) {
  const { lang, toggleLanguage } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState("choice");

  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [loginName, setLoginName] = useState("");

  const [regPhone, setRegPhone] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");

  const [loginIdTouched, setLoginIdTouched] = useState(false);
  const [regPhoneTouched, setRegPhoneTouched] = useState(false);
  const [uiError, setUiError] = useState("");

  const isEmail = (v = "") => v.includes("@");

  const formatLbLocal = (digits) => {
    // expects up to 8 digits => "DD DDD DDD"
    const d = digits.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    return [p1, p2, p3].filter(Boolean).join(" ");
  };

  const formatLbPlus961 = (digitsAfter961) => {
    // expects up to 8 digits after +961 => "+961 DD DDD DDD"
    const d = digitsAfter961.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    const parts = [p1, p2, p3].filter(Boolean).join(" ");
    return parts ? `+961 ${parts}` : `+961`;
  };

  const formatPhoneInput = (raw) => {
    const v = (raw || "").trim();

    // +961 flow (force formatting)
    if (v.startsWith("+961")) {
      const after = v.replace(/\s/g, "").slice(4); // remove spaces, remove +961
      const digits = after.replace(/\D/g, "");
      return formatLbPlus961(digits);
    }

    // local LB flow (0/7/8) (force formatting)
    // NOTE: we look at first non-space char
    const firstChar = v.replace(/\s/g, "").charAt(0);
    if (firstChar === "0" || firstChar === "7" || firstChar === "8") {
      const digits = v.replace(/\D/g, "").slice(0, 8);
      return formatLbLocal(digits);
    }

    // international / other -> do not change
    return raw;
  };

  const validatePhone = (value) => {
    const v = (value || "").trim();

    if (v.startsWith("+961")) {
      const all = v.replace(/\s/g, "");
      const after = all.slice(4); // after +961
      const digits = after.replace(/\D/g, "");
      if (digits.length !== 8) {
        return "Phone must be +961 followed by 8 digits.";
      }
      return null;
    }

    const compact = v.replace(/\s/g, "");
    const firstChar = compact.charAt(0);

    if (firstChar === "0" || firstChar === "7" || firstChar === "8") {
      const digits = compact.replace(/\D/g, "");
      if (digits.length !== 8) {
        return "Phone must be exactly 8 digits.";
      }
      return null;
    }

    // international/other: no forced validation (as requested)
    return null;
  };

  const handleRegister = async (e) => {
  e.preventDefault();

  try {
    const err = validatePhone(regPhone);
    if (err) {
      setUiError(err);
      return;
    }

    const res = await apiRequest("/auth/register", {
      method: "POST",
      body: JSON.stringify({
        full_name: regName,
        phone: regPhone,
        email: regEmail || null,
      }),
    });

    localStorage.setItem("esill-token", res.token);

    onClose();
    window.dispatchEvent(new Event("auth-changed"));
  } catch (err) {
    setUiError(err.message);
  }
};

const handleLogin = async (e) => {
  e.preventDefault();

  try {
    const res = await apiRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        identifier: loginIdentifier,
        full_name: loginName,
      }),
    });

    // ✅ store token
    localStorage.setItem("esill-token", res.token);

    // ✅ 🔥 THIS IS THE FIX (VERY IMPORTANT)
    await apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "login_sync",
      }),
    });

    // optional: close modal
    onClose();

    // optional: refresh UI
    window.dispatchEvent(new Event("auth-changed"));

  } catch (err) {
    setUiError(err.message);
  }
};

  useEffect(() => {
  if (isOpen && localStorage.getItem("esill-open-login")) {
    setMode("login");
    localStorage.removeItem("esill-open-login");
  }
}, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`
        fixed inset-0 z-[99999]
        flex items-center justify-center
        backdrop-blur-xl
        transition-opacity duration-300
        ${isOpen ? "opacity-100" : "opacity-0"}
      `}
      style={{
        background:
          "linear-gradient(135deg, rgba(30,136,229,0.35), rgba(229,57,53,0.35))",
      }}
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* CLOSE BUTTON */}
      <button
        onClick={onClose}
        className={`
          absolute top-8 text-3xl
          ${lang === "EN" ? "right-8" : "left-8"}
        `}
        style={{ color: "#ffffff" }}
      >
        ×
      </button>

      {/* LANGUAGE TOGGLE */}
      <button
        onClick={toggleLanguage}
        className={`
          absolute top-8 
          ${lang === "EN" ? "left-8" : "right-8"}
          px-4 py-1 rounded-full shadow-md text-sm font-semibold
        `}
        style={{
          background: "#ffffff",
          color: "#111827",
        }}
      >
        {lang === "EN" ? "AR" : "EN"}
      </button>

      {/* CARD */}
      <div
        className="
          w-[90%] max-w-sm
          bg-white
          rounded-3xl
          shadow-[0_25px_60px_rgba(0,0,0,0.25)]
          px-6 py-8
          animate-slideUp
        "
      >
        {/* ---------------- CHOICE MODE ---------------- */}
        {mode === "choice" && (
          <>
            <h2 className="text-xl font-bold text-center mb-3 text-gray-900">
              {lang === "EN" ? "Welcome to CHIACA" : "مرحباً بك في CHIACA"}
            </h2>

            <p className="text-sm text-center text-gray-600 mb-6">
              {lang === "EN"
                ? "Login or create an account to track orders and enjoy fun rewards!"
                : "سجّل دخولك أو أنشئ حساباً لمتابعة طلباتك والاستمتاع بالمكافآت!"}
            </p>

            <button
              onClick={() => {
                setUiError("");
                setMode("login");
              }}
              className="
                w-full py-3 mb-3 rounded-full
                text-white font-semibold
                transition-all duration-200
                hover:scale-[1.02]
              "
              style={{
                background: "linear-gradient(90deg,#E53935,#1E88E5)",
              }}
            >
              {lang === "EN" ? "Login" : "تسجيل الدخول"}
            </button>

            <button
              onClick={() => {
                setUiError("");
                setMode("register");
              }}
              className="
                w-full py-3 rounded-full
                font-semibold
                border-2
                transition-all duration-200
                hover:bg-gray-50
              "
              style={{
                borderColor: "#1E88E5",
                color: "#1E88E5",
              }}
            >
              {lang === "EN" ? "Create Account" : "إنشاء حساب"}
            </button>

            <button
              onClick={onClose}
              className="w-full text-center text-sm mt-6 text-gray-500 hover:text-gray-700"
            >
              {lang === "EN" ? "Continue as Guest" : "المتابعة كضيف"}
            </button>
          </>
        )}

        {/* ---------------- LOGIN MODE ---------------- */}
        {mode === "login" && (
          <>
            <button
              onClick={() => setMode("choice")}
              className="text-xs text-gray-500 mb-3"
            >
              ← {lang === "EN" ? "Back" : "رجوع"}
            </button>

            <h2 className="text-lg font-bold mb-4 text-gray-900">
              {lang === "EN"
                ? "Login to your account"
                : "تسجيل الدخول إلى حسابك"}
            </h2>

            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="text"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                placeholder={
                  lang === "EN"
                    ? "Email or phone"
                    : "البريد الإلكتروني أو الهاتف"
                }
                className="
                  w-full rounded-xl border
                  px-4 py-3 text-sm
                  focus:outline-none
                "
                style={{ borderColor: "#E5E7EB" }}
              />

              <input
                type="text"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder={
                  lang === "EN" ? "Full name" : "الاسم الكامل"
                }
                className="
                  w-full rounded-xl border
                  px-4 py-3 text-sm
                  focus:outline-none
                "
                style={{ borderColor: "#E5E7EB" }}
              />

              {uiError && (
                <div className="text-sm text-red-600">
                  {uiError}
                </div>
              )}

              <button
                type="submit"
                className="
                  w-full py-3 mt-2 rounded-full
                  text-white font-semibold
                  transition-all duration-200
                  hover:scale-[1.02]
                "
                style={{
                  background: "linear-gradient(90deg,#E53935,#1E88E5)",
                }}
              >
                {lang === "EN" ? "Login" : "تسجيل الدخول"}
              </button>
            </form>
          </>
        )}

        {/* ---------------- REGISTER MODE ---------------- */}
        {mode === "register" && (
          <>
            <button
              onClick={() => setMode("choice")}
              className="text-xs text-gray-500 mb-3"
            >
              ← {lang === "EN" ? "Back" : "رجوع"}
            </button>

            <h2 className="text-lg font-bold mb-4 text-gray-900">
              {lang === "EN"
                ? "Create your account"
                : "إنشاء حساب جديد"}
            </h2>

            <form onSubmit={handleRegister} className="space-y-4">
              <input
                type="tel"
                value={regPhone}
                onChange={(e) => setRegPhone(e.target.value)}
                placeholder={
                  lang === "EN" ? "Phone number" : "رقم الهاتف"
                }
                className="
                  w-full rounded-xl border
                  px-4 py-3 text-sm
                  focus:outline-none
                "
                style={{ borderColor: "#E5E7EB" }}
              />

              <input
                type="text"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder={
                  lang === "EN" ? "Full name" : "الاسم الكامل"
                }
                className="
                  w-full rounded-xl border
                  px-4 py-3 text-sm
                  focus:outline-none
                "
                style={{ borderColor: "#E5E7EB" }}
              />

              <input
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                placeholder={
                  lang === "EN"
                    ? "Email (optional)"
                    : "البريد الإلكتروني (اختياري)"
                }
                className="
                  w-full rounded-xl border
                  px-4 py-3 text-sm
                  focus:outline-none
                "
                style={{ borderColor: "#E5E7EB" }}
              />

              {uiError && (
                <div className="text-sm text-red-600">
                  {uiError}
                </div>
              )}

              <button
                type="submit"
                className="
                  w-full py-3 mt-2 rounded-full
                  text-white font-semibold
                  transition-all duration-200
                  hover:scale-[1.02]
                "
                style={{
                  background: "linear-gradient(90deg,#E53935,#1E88E5)",
                }}
              >
                {lang === "EN" ? "Register" : "إنشاء حساب"}
              </button>
            </form>
          </>
        )}
      </div>

      {/* Animation */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default EntryModal;
